import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { AiIcon } from '@plato/ui-legacy/assets/icons/ai-icon';
import { Skeleton } from '@plato/ui-legacy/components/ui/skeleton';
import Text from '@plato/ui-legacy/components/ui/text';
import { Button } from '@plato/ui/primitives/button';
import { SparklesIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveActiveMetric } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DaySuggestionCard } from '../../events/components/day-suggestion-card.tsx';
import { useHiddenSuggestions } from '../../events/hooks/use-hidden-suggestions.ts';

const COLLAPSED_SIZE = 3;
const EXPANDED_SIZE = 10;

type Props = {
    day: string;
    ownerId: string;
};

/**
 * Route-anchored customer suggestions for a single day, rendered inside that day's calendar column.
 * Collapsed behind a "Besuche vorschlagen" button until asked for, as in the design; the suggestions
 * are only fetched then. Callers render it only on days that have events: without a visit to anchor
 * on, the query can only return nationwide (Tier E) suggestions.
 */
export function DaySuggestions({ day, ownerId }: Props): ReactNode {
    const { t } = useTranslation('events');
    const [isRevealed, setIsRevealed] = useState(false);

    if (isRevealed) {
        return <DaySuggestionsList day={day} ownerId={ownerId} />;
    }

    return (
        <div className="flex justify-center">
            <Button
                onClick={() => {
                    setIsRevealed(true);
                }}
            >
                <SparklesIcon className="size-4" />
                {t('calendar_day_suggestions_reveal')}
            </Button>
        </div>
    );
}

/**
 * The top few customers-without-events; the query anchors tiering on that day's scheduled visits.
 */
function DaySuggestionsList({ day, ownerId }: Props): ReactNode {
    const { t } = useTranslation('events');
    const appConfig = useAppConfig();
    const { userId } = appConfig;
    const { billingStatus } = resolveActiveMetric(appConfig);
    const { isHidden } = useHiddenSuggestions();
    const apiUtils = api.useUtils();
    const sendEvent = useAnalytics();
    const [expanded, setExpanded] = useState(false);

    const { data: suggestions, isFetching } =
        api.platoUser.query.customers.getCustomersWithoutEvents.useQuery({
            dateFrom: day,
            dateTo: day,
            ownerId,
            limit: EXPANDED_SIZE,
            nearestTiersOnly: true,
            billingStatus,
        });

    useEffect(() => {
        if (suggestions && suggestions.length > 0) {
            sendEvent('recommended_customer_list_rendered', {
                customer_ids: suggestions.map((customer) => customer.customerId),
                customer_count: suggestions.length,
            });
        }
    }, [suggestions, sendEvent]);

    const handleEventCreated = (): void => {
        void apiUtils.platoUser.query.customers.getCustomersWithoutEvents.invalidate();
        void apiUtils.platoUser.query.events.getList.invalidate();
    };

    const header = (
        <div className="flex items-center gap-1">
            <AiIcon />
            <Text size="sm" weight="medium" color="neutral-700">
                {t('calendar_day_suggestions_title')}
            </Text>
        </div>
    );

    if (isFetching || !suggestions) {
        return (
            <div className="px-2">
                {header}
                <div className="divide-y divide-neutral-100">
                    {Array.from({ length: COLLAPSED_SIZE }).map((_, index) => (
                        <div key={index} className="flex flex-col gap-2 py-2">
                            <Skeleton className="h-4 w-1/2 rounded bg-content/(--opacity-wash)" />
                            <Skeleton className="h-3 w-3/4 rounded bg-content/(--opacity-wash)" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const visible = suggestions.filter((customer) => !isHidden(customer.customerId));

    if (visible.length === 0) {
        return (
            <div className="flex flex-col px-2 gap-1">
                {header}
                <Text size="sm" color="neutral-500" className="py-2">
                    {t('calendar_day_suggestions_empty')}
                </Text>
            </div>
        );
    }

    const visibleSuggestions = expanded ? visible : visible.slice(0, COLLAPSED_SIZE);
    const canToggle = visible.length > COLLAPSED_SIZE;

    return (
        <div className="flex flex-col px-2 gap-1">
            {header}
            <div className="divide-y divide-neutral-100">
                {visibleSuggestions.map((customer, index) => (
                    <DaySuggestionCard
                        key={customer.customerId}
                        customer={customer}
                        rank={index}
                        date={day}
                        onEventCreated={handleEventCreated}
                        hideScheduleButton={ownerId !== userId}
                    />
                ))}
            </div>
            {canToggle && (
                <Button
                    variant="secondary"
                    className="w-full mt-1"
                    onClick={() => {
                        setExpanded((current) => !current);
                    }}
                >
                    {expanded
                        ? t('calendar_day_suggestions_show_less')
                        : t('calendar_day_suggestions_show_more')}
                </Button>
            )}
        </div>
    );
}
